// Shared constants for the Networking Hub feature.
// Centralized here so that adding/renaming a connection type only requires
// changing one file — every dropdown, display, and type check picks it up.

// How close the connection is, in the client's own Momentum Maximizer
// vocabulary: first, second and third level. It replaced a five-value list
// that mixed degrees of separation with how you know someone ('alumni',
// 'former colleague', 'close friend') — two different questions on one
// dropdown, and the workbook only ever asked the first.
export const CONNECTION_TYPE_OPTIONS = [
  { value: '1st_level', label: '1st Level' },
  { value: '2nd_level', label: '2nd Level' },
  { value: '3rd_level', label: '3rd Level' },
] as const

export type ConnectionType = (typeof CONNECTION_TYPE_OPTIONS)[number]['value']

export const DEFAULT_CONNECTION_TYPE: ConnectionType = '1st_level'

// ---------------------------------------------------------------------------
// Outreach status
// ---------------------------------------------------------------------------

// The Momentum Maximizer's three outreach statuses, verbatim. Status is never
// conveyed by colour alone — the chip always carries the label too.
//
// All three describe an outreach that has happened, so a contact nobody has
// reached out to yet carries NO status at all. That is deliberate: the LinkedIn
// import brings in hundreds of connections, and stamping them all 'pending'
// would make "never touched" indistinguishable from "messaged, awaiting reply"
// — a distinction the weekly touchpoint goal depends on.
export const CONTACT_STATUS_OPTIONS = [
  { value: 'contacted', label: 'Contacted', chipColor: 'success' },
  { value: 'pending', label: 'Pending', chipColor: 'warning' },
  { value: 'not_comfortable', label: 'Not Comfortable Reaching Out', chipColor: 'default' },
] as const

export type ContactStatus = (typeof CONTACT_STATUS_OPTIONS)[number]['value']

/** How an absent status reads to staff and to the client. */
export const CONTACT_STATUS_UNSET_LABEL = 'Not yet actioned'

/**
 * Sentinel for the "no status yet" choice in the status filter. A filter value
 * has to be a string, and '' already means "all statuses".
 */
export const CONTACT_STATUS_UNSET_FILTER = '__unset__'

/**
 * Whether a contact passes the status filter row. Shared by the staff tab and
 * the client portal so the two cannot disagree about what a filter selects —
 * they previously each carried their own `c.status === statusFilter`, which had
 * no way to express "not yet actioned".
 */
export function matchesStatusFilter(status: string | undefined, filter: string): boolean {
  if (!filter) return true
  if (filter === CONTACT_STATUS_UNSET_FILTER) return !status
  return status === filter
}

/**
 * Whether this contact counts as outreach for the weekly touchpoint goal.
 * Reaching out is what counts, so both 'contacted' and 'pending' qualify;
 * 'not_comfortable' records a decision not to reach out, and no status means
 * nobody has tried yet.
 */
export function countsAsOutreach(status?: string): boolean {
  return !!status && status !== 'not_comfortable'
}

// ---------------------------------------------------------------------------
// Outreach stages (checklists and templates)
// ---------------------------------------------------------------------------

// Checklists and templates are keyed by *stage*, which is the contact's status
// plus one more: the pre-outreach stage a contact sits in before anyone has
// acted. OutreachChecklist resolves it as `contactStatus || 'not_contacted'`,
// so 'not_contacted' survives here as a stage even though it is no longer a
// status — a "before you reach out" checklist is genuinely useful, and it is
// what an unactioned contact should see.
export const OUTREACH_STAGE_OPTIONS = [
  { value: 'not_contacted', label: 'Before reaching out' },
  ...CONTACT_STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
] as const

export type OutreachStage = (typeof OUTREACH_STAGE_OPTIONS)[number]['value']

/** The stage an unactioned contact's checklist is filed under. */
export const PRE_OUTREACH_STAGE: OutreachStage = 'not_contacted'

/**
 * A contact's reach-out instruction time, for display.
 *
 * Shared by the contacts table and the contact detail panel so the client and
 * their strategist read the same wording off the same value. Takes a full ISO
 * timestamp (not a date-only string, so `new Date` resolves the zone
 * correctly) and returns '' for anything absent or unparseable — every caller
 * treats '' as "no instruction" rather than rendering "Invalid Date".
 */
export function formatReachOutAt(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Interaction types
// ---------------------------------------------------------------------------

// What happened with a contact, in the client's own vocabulary. Replaced a
// six-value list that mixed channels with a catch-all: 'message_sent' and
// 'outreach_sent' both meant "we sent something" without saying where, and
// 'note' was a comment rather than an interaction.
//
// Shared with the interview follow-up timeline on purpose. The two flows write
// the same contactInteractions record through the same lambda, and the comment
// in InterviewFollowUpTimeline has always said the vocabulary of "what
// happened" is deliberately one list.
export const INTERACTION_TYPE_OPTIONS = [
  { value: 'linkedin_message', label: 'LinkedIn Message' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
] as const

export type InteractionType = (typeof INTERACTION_TYPE_OPTIONS)[number]['value']

export const DEFAULT_INTERACTION_TYPE: InteractionType = 'linkedin_message'

/**
 * Label for a stored interaction type. Retired values — message_sent,
 * outreach_sent, note — are deliberately NOT migrated: they record what
 * actually happened, and rewriting an 'outreach_sent' as a LinkedIn message
 * would invent a channel nobody recorded. They render humanised instead of as
 * raw snake_case.
 */
export function interactionTypeLabel(type: string): string {
  const known = INTERACTION_TYPE_OPTIONS.find((t) => t.value === type)
  return known ? known.label : type.replace(/_/g, ' ')
}

// MWA-297 — the date an interaction should be read and ordered by: when the
// conversation happened, falling back to when the row was written for records
// created before occurredAt existed. Every surface that renders or sorts
// interactions goes through this, so the two never disagree.
export function interactionDate(i: { occurredAt?: string; createdAt: string }): string {
  return i.occurredAt || i.createdAt
}

// Newest first, by the date the conversation happened.
export function sortInteractionsByDate<T extends { occurredAt?: string; createdAt: string }>(
  interactions: T[]
): T[] {
  return [...interactions].sort(
    (a, b) => Date.parse(interactionDate(b)) - Date.parse(interactionDate(a))
  )
}

// MWA-297 — the most recent conversation with one contact, from a list already
// loaded for the whole client. Returns null when nobody has spoken to them yet,
// which the caller renders as its own state rather than as a blank.
export function lastInteractionFor<T extends { contactId?: string; occurredAt?: string; createdAt: string }>(
  interactions: T[],
  contactId: string
): T | null {
  const mine = interactions.filter((i) => i.contactId === contactId)
  if (mine.length === 0) return null
  return sortInteractionsByDate(mine)[0]
}
